// Pure data layer of the Profesionales tab: professional -> form values, validations and request body.
import { DIAS_SEMANA } from "../reservas/primerUso/barPayload";

export const MSG_SOLAPE = "Los tramos de un mismo día no pueden solaparse";

export const idDe = (x) => String(x?._id || x?.serviceId || x?.id || x || "");

const aMin = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  return h * 60 + m;
};

const aHora = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

/** Next range proposal for a day: starts where the last one ends, two hours long (capped at 23:45). */
export const tramoSiguiente = (tramos) => {
  const ultimo = tramos[tramos.length - 1];
  if (!ultimo) return { open: "09:00", close: "13:00" };
  const open = aMin(ultimo.close);
  return { open: ultimo.close, close: aHora(Math.min(open + 120, 23 * 60 + 45)) };
};

export const horarioVacio = () =>
  Object.fromEntries(DIAS_SEMANA.map(({ weekday }) => [weekday, { enabled: false, tramos: [{ open: "09:00", close: "13:00" }] }]));

/** weeklySchedule (flat ranges) -> { [weekday]: { enabled, tramos } }. */
export const horarioDeLista = (lista) => {
  const h = horarioVacio();
  const porDia = {};
  (Array.isArray(lista) ? lista : []).forEach((t) => {
    if (t.enabled === false) return;
    (porDia[Number(t.weekday)] ||= []).push({ open: t.open, close: t.close });
  });
  Object.entries(porDia).forEach(([dia, tramos]) => {
    h[dia] = { enabled: true, tramos: tramos.sort((a, b) => aMin(a.open) - aMin(b.open)) };
  });
  return h;
};

/** Salon openingHours -> the same shape, to offer "Copiar horario del salón". */
export const horarioDelSalon = (salon) => horarioDeLista(salon?.openingHours);

const texto = (v) => (v == null ? "" : String(v));

/** Form values from a professional returned by the back (null = new). */
export const formDeProfesional = (p) => {
  const overrides = new Map((p?.serviceOverrides || []).map((o) => [idDe(o.serviceId), o]));
  const servicios = {};
  (p?.serviceIds || p?.services || []).forEach((s) => {
    const id = idDe(s);
    const o = overrides.get(id);
    // La comisión de cada servicio se edita en la pestaña Comisiones: acá viaja oculta para no perderla al guardar.
    servicios[id] = {
      precio: texto(o?.price),
      duracion: texto(o?.durationMinutes),
      sena: texto(o?.deposit),
      commissionType: o?.commissionType ?? null,
      commissionValue: o?.commissionValue ?? null,
    };
  });
  return {
    nombre: p?.name || "",
    especialidad: p?.specialty || "",
    duracionMaxima: texto(p?.maxAppointmentMinutes),
    activo: p ? p.active !== false : true,
    servicios,
    horario: p ? horarioDeLista(p.weeklySchedule) : horarioVacio(),
  };
};

export const hayOverlap = (tramos) => {
  const rangos = tramos
    .map((t) => ({ a: aMin(t.open), b: aMin(t.close) <= aMin(t.open) ? aMin(t.close) + 1440 : aMin(t.close) }))
    .sort((x, y) => x.a - y.a);
  return rangos.some((r, i) => i > 0 && r.a < rangos[i - 1].b);
};

const vacio = (v) => v === "" || v == null;

/** First problem found in the form, "" when fine. `catalogo` = salon services. */
export const faltaProfesional = (f, catalogo) => {
  if (!f.nombre.trim()) return "Falta el nombre del profesional";
  if (!vacio(f.duracionMaxima)) {
    const max = Number(f.duracionMaxima);
    if (!(Number.isInteger(max) && max >= 5 && max % 5 === 0)) return "La duración máxima del turno debe ser múltiplo de 5 minutos";
  }
  for (const [id, o] of Object.entries(f.servicios)) {
    const base = catalogo.find((s) => idDe(s) === id);
    const nombre = base?.name || "un servicio";
    if (!vacio(o.duracion) && !(Number.isInteger(Number(o.duracion)) && Number(o.duracion) >= 5 && Number(o.duracion) % 5 === 0)) {
      return `La duración propia de ${nombre} debe ser múltiplo de 5 minutos`;
    }
    if (!vacio(o.precio) && !(Number(o.precio) >= 0)) return `El precio propio de ${nombre} no puede ser negativo`;
    if (!vacio(o.sena)) {
      if (!(Number(o.sena) >= 0)) return `La seña propia de ${nombre} no puede ser negativa`;
      const precio = vacio(o.precio) ? Number(base?.price ?? 0) : Number(o.precio);
      if (Number(o.sena) > precio) return `La seña propia de ${nombre} supera su precio`;
    }
  }
  for (const { weekday, largo } of DIAS_SEMANA) {
    const d = f.horario[weekday];
    if (!d.enabled) continue;
    if (d.tramos.some((t) => !t.open || !t.close || t.open === t.close)) return `Revisá el horario del ${largo.toLowerCase()}`;
    if (hayOverlap(d.tramos)) return MSG_SOLAPE;
  }
  return "";
};

/** Body for /professionals/add | update. Empty override fields go as null (inherit the base value).
 * A locked deposit field keeps its loaded value, so editing never erases an existing override. */
export const payloadProfesional = (f) => {
  const aNum = (v) => (vacio(v) ? null : Number(v));
  const serviceIds = Object.keys(f.servicios);
  const serviceOverrides = serviceIds
    .map((serviceId) => {
      const o = f.servicios[serviceId];
      return {
        serviceId,
        price: aNum(o.precio),
        deposit: aNum(o.sena),
        durationMinutes: aNum(o.duracion),
        ...(o.commissionType != null ? { commissionType: o.commissionType, commissionValue: o.commissionValue } : {}),
      };
    })
    .filter(
      (o) => o.price !== null || o.deposit !== null || o.durationMinutes !== null || o.commissionType !== undefined
    );
  const weeklySchedule = DIAS_SEMANA.flatMap(({ weekday }) => {
    const d = f.horario[weekday];
    return d.enabled ? d.tramos.map((t) => ({ weekday, open: t.open, close: t.close, enabled: true })) : [];
  });
  return {
    name: f.nombre.trim(),
    specialty: (f.especialidad || "").trim(),
    maxAppointmentMinutes: aNum(f.duracionMaxima),
    active: f.activo,
    serviceIds,
    serviceOverrides,
    weeklySchedule,
  };
};
